import type React from "react";
import { createContext } from "react";

import type { AnalyticsConfig, AnalyticsEvent } from "../types";

interface AnalyticsContextValue {
  sendEvent: (eventName: string, properties?: AnalyticsEvent) => void;
  identifyUser: (userId: string, traits?: Record<string, any>) => void;
  updateUserProfile: (traits?: Record<string, any>) => void;
  setSuperProps: (props: Record<string, any>) => void;
  linkAnonymousToUser: (userId: string) => void;
  resetAnalytics: () => void;
  getDistinctId: () => void;
}

export const AnalyticsContext = createContext<
  AnalyticsContextValue | undefined
>(undefined);

export const AnalyticsProvider: React.FC<{
  config: AnalyticsConfig;
  children: React.ReactNode;
}> = ({ config, children }) => {
  const sendEvent = (eventName: string, properties?: AnalyticsEvent) => {
    config.providers.forEach((provider) => {
      // Get the provider type name for better error messages
      const providerName = provider.providerType || provider.constructor.name;

      try {
        if (config.debug) {
          console.log(`Sending event to ${providerName}: ${eventName}`);
        }
        provider.sendEvent(eventName, properties);
      } catch (error) {
        console.error(`Error sending event to ${providerName}:`, error);
      }
    });
  };

  const identifyUser = (userId: string, traits?: Record<string, any>) => {
    config.providers.forEach((provider) => {
      // Get the provider type name for better error messages
      const providerName = provider.providerType || provider.constructor.name;

      // Skip if the provider doesn't implement identify
      if (!provider.identify) {
        console.debug(
          `Provider ${providerName} does not support user identification`,
        );
        return;
      }

      try {
        console.debug(`Identifying user in ${providerName}: ${userId}`);
        provider.identify(userId, traits);
      } catch (error) {
        console.error(`Error identifying user in ${providerName}:`, error);
      }
    });
  };

  const updateUserProfile = (traits?: Record<string, any>) => {
    config.providers.forEach((provider) => {
      if (!provider.updateUserProfile) return;
      try {
        provider.updateUserProfile(traits);
      } catch (error) {
        console.error(
          `Error updating profile in ${provider.providerType}:`,
          error,
        );
      }
    });
  };

  // NEW: alias-once (signup only)
  const linkAnonymousToUser = (userId: string) => {
    config.providers.forEach((provider) => {
      if (!provider.linkAnonymousToUser) return;
      try {
        provider.linkAnonymousToUser(userId);
      } catch (error) {
        console.error(
          `Error linking anonymous id in ${provider.providerType}:`,
          error,
        );
      }
    });
  };

  // NEW: reset on logout
  const reset = () => {
    config.providers.forEach((provider) => {
      if (!provider.reset) return;
      try {
        provider.reset();
      } catch (error) {
        console.error(`Error resetting ${provider.providerType}:`, error);
      }
    });
  };

  // NEW: set super properties (useful after auth or plan changes)
  const setSuperProps = (props: Record<string, any>) => {
    config.providers.forEach((provider) => {
      if (!provider.setSuperProps) return;
      try {
        provider.setSuperProps(props);
      } catch (error) {
        console.error(
          `Error setting super props in ${provider.providerType}:`,
          error,
        );
      }
    });
  };

  // NEW: get the current client id (if a provider supports it)
  const getDistinctId = (): string | undefined => {
    for (const provider of config.providers) {
      if (provider.getDistinctId) {
        try {
          const id = provider.getDistinctId();
          if (id) return id;
        } catch {
          /* ignore */
        }
      }
    }
    return undefined;
  };

  return (
    <AnalyticsContext.Provider
      value={{
        sendEvent,
        identifyUser,
        updateUserProfile,
        resetAnalytics: reset,
        linkAnonymousToUser,
        getDistinctId,
        setSuperProps,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
